// import { notifyUser } from '~src/utils';
import { getState, modifyState } from '~src/state';
import browserLocalstorage from 'browser-localstorage-expire';
import editorConfig from '~config/editorConfig';
import { USER_CHANGED_EVENT } from './events';
import {
  assertOfcAccessActive,
  clearParseSession,
  getCurrentParseUser,
  getStoredParseSessionToken,
  logoutParseSession,
  invalidatePendingSession,
} from '~auth/parseSession';

const localCache = browserLocalstorage();
const USER_CONTEXT_CACHE_KEY = 'parseUserContext';

// Fields that must never reach the contour cache or the returned context.
// `/users/me` echoes the session token back on the user object.
const CREDENTIAL_FIELDS = [
  'sessionToken',
  'password',
  'kmsEncryptedPassword',
  'token',
  'authData',
];

let userContextPromise;
let contextGeneration = 0;
let validatedSession;

const modifyUserStateIfChanged = (user, { refresh = false } = {}) => {
  if (!user) {
    return;
  }

  const currentUserId = getState('user.objectId');
  const currentOrgId = getState('user.org_pointer.objectId');
  const nextUserId = user.objectId;
  const nextOrgId = user.org_pointer?.objectId;
  const identityChanged =
    currentUserId !== nextUserId || currentOrgId !== nextOrgId;

  if (!identityChanged && !refresh) {
    return;
  }

  // A network refresh must always publish the latest fields, but only an
  // identity change is a user change for observers (iframes, caches, ...).
  if (
    identityChanged &&
    (currentUserId || currentOrgId) &&
    typeof window !== 'undefined' &&
    typeof window.dispatchEvent === 'function'
  ) {
    window.dispatchEvent(new window.Event(USER_CHANGED_EVENT));
  }

  modifyState('user', user);
};

const sanitizeUserContext = (user) => {
  if (!user || typeof user !== 'object') {
    return user;
  }

  return CREDENTIAL_FIELDS.reduce(
    (sanitized, field) => {
      delete sanitized[field];
      return sanitized;
    },
    { ...user }
  );
};

const buildUserContext = (user, sessionToken) => ({
  user,
  userToken: sessionToken,
  ofcSuperUser: user?.ofcSuperUser === true,
  gleapUserHash: user?.gleapUserHash,
  isOrgNew: false,
  editorCanvas: undefined,
});

const fetchUserContext = async () => {
  const generation = contextGeneration;
  const { sessionToken, user } = await getCurrentParseUser();
  if (generation !== contextGeneration)
    throw new Error('Session operation cancelled');
  const sanitizedUser = sanitizeUserContext(user);
  validatedSession = sessionToken;

  modifyUserStateIfChanged(sanitizedUser, { refresh: true });
  localCache.setItem(
    USER_CONTEXT_CACHE_KEY,
    buildUserContext(sanitizedUser),
    editorConfig.localStorageExpiryInMinutes
  );

  return buildUserContext(sanitizedUser, sessionToken);
};

const getDirectUser = async (ignoreCache) => {
  // An Argo access (PE-2215) ends at its expiry even when the contour is still
  // cached: the operator is sent back to Argo instead of working on.
  await assertOfcAccessActive();

  // Drop the credential-bearing cache left by Giotto versions that still
  // bootstrapped through the User API.
  localStorage.removeItem('startup');

  if (ignoreCache) {
    localStorage.removeItem(USER_CONTEXT_CACHE_KEY);
    localStorage.removeItem('disabledFeatures');
  }

  const sessionToken = getStoredParseSessionToken();
  const cachedUserContext = localCache.getItem(USER_CONTEXT_CACHE_KEY);

  if (
    !ignoreCache &&
    sessionToken &&
    validatedSession === sessionToken &&
    cachedUserContext?.user
  ) {
    modifyUserStateIfChanged(cachedUserContext.user);
    return {
      ...cachedUserContext,
      userToken: sessionToken,
    };
  }

  if (!userContextPromise) {
    const pending = fetchUserContext().finally(() => {
      if (userContextPromise === pending) userContextPromise = null;
    });
    userContextPromise = pending;
  }

  return userContextPromise;
};

export const getUser = async (payload = {}, ignoreCache = false) => {
  // Profile mutations used to travel inside the /startup payload. The direct
  // flow only reads the contour, so a payload is a programming error.
  if (payload && Object.keys(payload).length > 0) {
    throw new Error('getUser no longer accepts /startup payloads');
  }

  return getDirectUser(ignoreCache);
};

export const resetUserSession = async ({ revoke = false } = {}) => {
  contextGeneration += 1;
  validatedSession = undefined;
  invalidatePendingSession();
  localStorage.removeItem('startup');
  localStorage.removeItem(USER_CONTEXT_CACHE_KEY);
  localStorage.removeItem('disabledFeatures');
  localStorage.removeItem('userFeatures');
  userContextPromise = null;

  if (revoke) {
    await logoutParseSession();
  } else {
    clearParseSession();
  }
};

export const isLoggedUserGlobalEditor = async () => {
  const { user } = await getUser();

  return user && user.giottoGlobalAdmin === true;
};

export const isLoggedUserSuperUser = async () => {
  const { user, ofcSuperUser } = await getUser();

  return (
    ofcSuperUser === true ||
    (user && editorConfig.superUserIds.includes(user.objectId))
  );
};

export const isUserAuthorized = async () => {
  // const { user } = await getUser();
  // if (user.org_pointer.tipo_licenza === 'plus') {
  //   await notifyUser();
  // }
};

export * from './dispalySubscritionDetails';
